import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getChildren, getChildDocumentRequests, respondToChildDocumentRequest } from '@/api/parent';
import { FileText, Upload, AlertCircle, CheckCircle2, Clock, X } from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function DocumentRequestsPage() {
  const qc = useQueryClient();
  const [selectedChild, setSelectedChild] = useState<number | null>(null);
  const [respondTo, setRespondTo] = useState<any | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');

  const childrenQ = useQuery({
    queryKey: ['parent-children'],
    queryFn: () => getChildren().then((r: any) => r.data?.data ?? r.data),
  });
  const children = arr<any>(childrenQ.data);

  const requestsQ = useQuery({
    queryKey: ['parent-doc-requests'],
    queryFn: async () => {
      const results = await Promise.all(
        children.map(async (c: any) => {
          try {
            const r = await getChildDocumentRequests(c.id);
            return { child: c, requests: arr<any>(r.data?.data ?? r.data) };
          } catch {
            return { child: c, requests: [] };
          }
        })
      );
      return results;
    },
    enabled: children.length > 0,
  });

  const data = requestsQ.data ?? [];
  const filtered = selectedChild
    ? data.filter((d: any) => d.child.id === selectedChild)
    : data;

  const respondM = useMutation({
    mutationFn: ({ childId, requestId, f, n }: any) =>
      respondToChildDocumentRequest(childId, requestId, f, n),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parent-doc-requests'] });
      setRespondTo(null);
      setFile(null);
      setNote('');
      alert('Document sent to the school.');
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Upload failed'),
  });

  const counts = useMemo(() => {
    let pending = 0, total = 0;
    data.forEach((d: any) => d.requests.forEach((r: any) => {
      total++;
      if (r.status === 'pending' || r.status === 'declined') pending++;
    }));
    return { pending, total };
  }, [data]);

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Documents</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Documents the school needs</h1>
        <p className="text-slate-500 text-sm mt-1">
          Files the school has asked you to send for your child
        </p>
      </div>

      {children.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedChild(null)}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${
              selectedChild === null
                ? 'bg-[#0f172a] text-white border-[#0f172a]'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            All children
          </button>
          {children.map((c: any) => (
            <button
              key={c.id}
              onClick={() => setSelectedChild(c.id)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${
                selectedChild === c.id
                  ? 'bg-[#0f172a] text-white border-[#0f172a]'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {c.first_name ?? c.name ?? '—'} {c.last_name ?? ''}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Awaiting your action</p>
          <p className="text-3xl font-extrabold text-amber-600">{counts.pending}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Total requests</p>
          <p className="text-3xl font-extrabold text-slate-900">{counts.total}</p>
        </div>
      </div>

      {requestsQ.isLoading ? (
        <p className="text-sm text-slate-500 text-center py-8">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-10 rounded-xl border border-slate-200 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No document requests yet</p>
          <p className="text-sm text-slate-500">
            When the school needs a document from you, it will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(({ child, requests }: any) => (
            <div key={child.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {child.first_name ?? child.name ?? '—'} {child.last_name ?? ''}
                  </p>
                  <p className="text-xs text-slate-500">
                    {child.admission_no ?? '—'}
                    {child.current_class?.name ? ` · ${child.current_class.name}` : ''}
                  </p>
                </div>
                <span className="text-xs font-semibold text-slate-500">
                  {requests.length} request{requests.length === 1 ? '' : 's'}
                </span>
              </div>
              {requests.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-500">No requests for this child.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {requests.map((r: any) => (
                    <li key={r.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 capitalize flex items-center gap-2">
                          {r.document_type?.replace(/_/g, ' ')}
                          <StatusChip status={r.status} />
                        </p>
                        {r.note && (
                          <p className="text-xs text-slate-500 mt-0.5">School note: {r.note}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          Requested {fmtDate(r.created_at)}
                          {r.responded_at && ` · You replied ${fmtDate(r.responded_at)}`}
                        </p>
                        {r.declined_reason && (
                          <p className="text-xs text-red-600 mt-1">Declined: {r.declined_reason}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {r.document_url && (
                          <a
                            href={r.document_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                          >
                            View your file
                          </a>
                        )}
                        {(r.status === 'pending' || r.status === 'declined') && (
                          <button
                            onClick={() => setRespondTo({ ...r, childId: child.id })}
                            className="px-3 py-1.5 text-xs font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" /> Upload
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {respondTo && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-slate-900">Send document to school</h3>
              <button
                onClick={() => { setRespondTo(null); setFile(null); setNote(''); }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Document needed</p>
              <p className="font-semibold text-slate-900 capitalize">
                {respondTo.document_type?.replace(/_/g, ' ')}
              </p>
              {respondTo.note && (
                <p className="text-xs text-slate-500 mt-1">School says: {respondTo.note}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Choose file</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">PDF, JPG or PNG · max 5 MB</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Note (optional)</label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm"
                placeholder="Any message for the school"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setRespondTo(null); setFile(null); setNote(''); }}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                disabled={!file || respondM.isPending}
                onClick={() => respondM.mutate({
                  childId: respondTo.childId,
                  requestId: respondTo.id,
                  f: file!,
                  n: note.trim() || undefined,
                })}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50"
              >
                {respondM.isPending ? 'Sending…' : 'Send to school'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; tone: string; icon: React.ReactNode }> = {
    pending:   { label: 'Waiting',  tone: 'bg-amber-50 text-amber-700 border-amber-200',  icon: <Clock className="w-3 h-3" /> },
    responded: { label: 'Sent',     tone: 'bg-blue-50 text-blue-700 border-blue-200',     icon: <Upload className="w-3 h-3" /> },
    accepted:  { label: 'Accepted', tone: 'bg-green-50 text-green-700 border-green-200',  icon: <CheckCircle2 className="w-3 h-3" /> },
    declined:  { label: 'Declined', tone: 'bg-red-50 text-red-700 border-red-200',        icon: <AlertCircle className="w-3 h-3" /> },
  };
  const s = map[status] ?? { label: status, tone: 'bg-slate-100 text-slate-600 border-slate-200', icon: null };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.tone}`}>
      {s.icon} {s.label}
    </span>
  );
}
